import { DOCUMENT, NgOptimizedImage } from "@angular/common";
import { CdkTrapFocus } from "@angular/cdk/a11y";
import { CdkOverlayOrigin, Overlay, OverlayModule, STANDARD_DROPDOWN_BELOW_POSITIONS } from "@angular/cdk/overlay";
import { afterNextRender, ChangeDetectionStrategy, Component, computed, ElementRef, inject, Injector, input, signal, viewChild } from "@angular/core";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { TranslatePipe } from "@ngx-translate/core";
import type { Category } from "@ophardt/shopware-api";
import { IconComponent } from "@shared/components/atoms/icon/icon.component";
import { buildCategoryPath } from "@shared/helpers/build-category-path.helper";
import { SmallestThumbnailPipe } from "@shared/pipes/smallest-thumbnail.pipe";
import { CategoryMenuGroupsComponent } from "./category-menu-groups/category-menu-groups.component";
import { CategoryProductsPreviewComponent } from "./category-products-preview/category-products-preview.component";

@Component({
  selector: "app-product-category-menu",
  templateUrl: "./product-category-menu.component.html",
  styleUrl: "./product-category-menu.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgOptimizedImage,
    OverlayModule,
    CdkTrapFocus,
    TranslatePipe,
    RouterLink,
    SmallestThumbnailPipe,
    IconComponent,
    CategoryMenuGroupsComponent,
    CategoryProductsPreviewComponent,
  ],
})
export class ProductCategoryMenuComponent {
  private readonly overlay = inject(Overlay);
  private readonly route = inject(ActivatedRoute);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);

  readonly overlayOrigin = input.required<CdkOverlayOrigin>();
  readonly categoryList = input.required<Category[]>();

  protected readonly menuTrigger = viewChild.required<ElementRef<HTMLButtonElement>>("menuTrigger");
  protected readonly menuPanel = viewChild<ElementRef<HTMLElement>>("menuPanel");

  protected readonly isOpened = signal(false);
  protected readonly selectedCategoryId = signal<string | null>(null);
  protected readonly activeCategory = computed(() => {
    const categories = this.categoryList();
    return categories.find((category) => category.id === this.selectedCategoryId()) ?? categories[0];
  });

  protected readonly overlayPositions = STANDARD_DROPDOWN_BELOW_POSITIONS;
  protected readonly scrollStrategy = this.overlay.scrollStrategies.block();

  protected toggleMenu(): void {
    if (this.isOpened()) {
      this.closeMenu();
      return;
    }

    this.openMenu();
  }

  protected openMenu(): void {
    if (this.isOpened()) {
      this.focusActiveRoot();
      return;
    }

    const categories = this.categoryList();
    const categoryId = this.route.snapshot.queryParamMap.get("category") ?? "";
    this.selectedCategoryId.set(buildCategoryPath(categories, categoryId)[0] ?? categories[0]?.id ?? null);
    const origin = this.overlayOrigin().elementRef.nativeElement as HTMLElement;
    this.document.documentElement.style.setProperty("--category-menu-top", `${Math.max(origin.getBoundingClientRect().bottom, 0)}px`);
    this.isOpened.set(true);
  }

  protected closeMenu(restoreFocus = false): void {
    if (!this.isOpened()) {
      return;
    }

    this.isOpened.set(false);

    if (restoreFocus) {
      this.menuTrigger().nativeElement.focus();
    }
  }

  protected onOverlayAttached(): void {
    afterNextRender(() => this.focusActiveRoot(), { injector: this.injector });
  }

  protected onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      event.preventDefault();
      this.closeMenu(true);
      return;
    }

    const menu = this.menuPanel()?.nativeElement;
    const target = event.target as HTMLElement;
    if (!menu) {
      return;
    }

    const pane = menu.querySelector<HTMLElement>(".subcategory-panel");
    if (event.key === "ArrowRight" && target.classList.contains("root-category")) {
      const firstLink = pane?.querySelector<HTMLElement>("a");
      if (!firstLink) {
        return;
      }

      event.preventDefault();
      firstLink.focus();
      return;
    }

    if (event.key === "ArrowLeft" && pane?.contains(target)) {
      event.preventDefault();
      this.focusActiveRoot();
    }
  }

  private focusActiveRoot(): void {
    this.menuPanel()?.nativeElement.querySelector<HTMLElement>(".root-category.active")?.focus();
  }
}
